import EmailIcon from "@mui/icons-material/Email";
import GroupsIcon from "@mui/icons-material/Groups";
import PhoneIcon from "@mui/icons-material/Phone";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";

import type { Practitioner } from "@/api/types";
import { PractitionerAvatar } from "@/components/PractitionerAvatar";
import { StatusChip } from "@/components/StatusChip";

export function PractitionerCardGrid({
  rows,
  loading,
  selection,
  onToggleSelect,
  onShowMemberships,
  onEdit,
  showSelection,
  canUpdate,
}: {
  rows: Practitioner[];
  loading: boolean;
  selection: Set<string>;
  onToggleSelect: (id: string) => void;
  onShowMemberships: (p: Practitioner) => void;
  onEdit: (p: Practitioner) => void;
  showSelection: boolean;
  canUpdate: boolean;
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Skeleton variant="rounded" height={170} />
          </Grid>
        ))}
      </Grid>
    );
  }
  if (rows.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("practitioners.emptyList")}
        </Typography>
      </Box>
    );
  }
  return (
    <Grid container spacing={2}>
      {rows.map((p) => {
        const selected = selection.has(p.id);
        return (
          <Grid key={p.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Card
              variant="outlined"
              sx={{
                position: "relative",
                borderColor: selected ? "primary.main" : "divider",
                bgcolor: selected ? "action.selected" : "background.paper",
                transition: "border-color 120ms",
              }}
            >
              {showSelection && (
                <Checkbox
                  size="small"
                  checked={selected}
                  onChange={() => onToggleSelect(p.id)}
                  sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
                />
              )}
              <CardActionArea onClick={() => onEdit(p)} disabled={!canUpdate}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <PractitionerAvatar
                      practitionerId={p.id}
                      hasPhoto={Boolean((p as Practitioner & { has_photo?: boolean }).has_photo)}
                      displayName={p.display_name}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700 }}
                        noWrap
                        title={p.display_name}
                      >
                        {p.display_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {p.rpps ?? "—"}
                      </Typography>
                    </Box>
                    <StatusChip value={p.status} />
                  </Stack>
                  <Stack spacing={0.5}>
                    {p.email && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <EmailIcon fontSize="inherit" color="action" />
                        <Typography variant="caption" noWrap title={p.email}>
                          {p.email}
                        </Typography>
                      </Stack>
                    )}
                    {p.phone && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <PhoneIcon fontSize="inherit" color="action" />
                        <Typography variant="caption">{p.phone}</Typography>
                      </Stack>
                    )}
                  </Stack>
                  <Box sx={{ mt: 1.5 }}>
                    {p.languages && p.languages.length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {p.languages.slice(0, 4).map((lng) => (
                          <Chip
                            key={lng}
                            size="small"
                            variant="outlined"
                            label={lng.toUpperCase()}
                            sx={{ height: 18, fontSize: 10, fontFamily: "monospace" }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
              <Stack
                direction="row"
                spacing={0.5}
                justifyContent="flex-end"
                sx={{ px: 1, pb: 1 }}
              >
                <Tooltip title={t("practitioners.memberships")}>
                  <IconButton size="small" onClick={() => onShowMemberships(p)}>
                    <GroupsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
